import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';

import { AlertTriangle, Loader2, ShieldCheck, UserCog, Users } from 'lucide-react';

import { UserRole } from '@/state/store/auth/index.type';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import type { SerializedUser } from './users-admin-client';

interface EditRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: SerializedUser | null;
  pendingRole: UserRole | null;
  onConfirm: (role: UserRole) => Promise<void>;
}

export function EditRoleDialog({
  open,
  onOpenChange,
  user,
  pendingRole,
  onConfirm,
}: EditRoleDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleConfirm = async () => {
    if (!pendingRole) return;

    setIsSaving(true);
    try {
      await onConfirm(pendingRole);
      onOpenChange(false);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : t('ADMIN_USERS_EDITROLEDIALOG_ERRORS_UPDATEFAILED');
      toast({ variant: 'destructive', title: 'Error', description: message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isSaving) {
      onOpenChange(newOpen);
    }
  };

  const userName =
    (user?.firstName || user?.lastName
      ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
      : user?.email) || t('ADMIN_USERS_EDITROLEDIALOG_UNKNOWNUSER');

  const getRoleInfo = (role: UserRole | null) => {
    if (role === 'sys_admin') {
      return {
        label: t('ADMIN_USERS_ROLES_ADMIN'),
        icon: <ShieldCheck className="size-4" />,
        color: 'text-selise-blue',
        bgColor: 'bg-selise-blue/10',
      };
    }
    if (role === 'sys_editor') {
      return {
        label: t('ADMIN_USERS_ROLES_EDITOR'),
        icon: <UserCog className="size-4" />,
        color: 'text-lime-green',
        bgColor: 'bg-lime-green/10',
      };
    }
    return {
      label: t('ADMIN_USERS_ROLES_NONE'),
      icon: <Users className="size-4" />,
      color: 'text-globe-grey',
      bgColor: 'bg-[hsl(var(--globe-grey))]/10',
    };
  };

  const newRoleInfo = getRoleInfo(pendingRole);

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="w-[95vw] max-w-lg max-h-[90dvh] overflow-y-auto p-4 sm:p-6 rounded-2xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-9 sm:size-10 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
              <AlertTriangle className="size-4 sm:size-5 text-amber-500" />
            </div>
            <AlertDialogTitle className="text-base sm:text-lg">
              {t('ADMIN_USERS_EDITROLEDIALOG_TITLE')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2 space-y-3 text-xs sm:text-sm">
            <p>
              {t('ADMIN_USERS_EDITROLEDIALOG_DESCRIPTION', {
                name: userName,
                oldRole: t('ADMIN_USERS_ROLES_ADMIN'),
                newRole: newRoleInfo.label,
              })}
            </p>
            <p>{t('ADMIN_USERS_EDITROLEDIALOG_REVOKENOTICE')}</p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 my-2">
          <p className="text-xs sm:text-sm text-amber-800">
            {t('ADMIN_USERS_EDITROLEDIALOG_NOTE')}
          </p>
        </div>

        <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <AlertDialogCancel disabled={isSaving} className="w-full sm:w-auto mt-0">
            {t('ADMIN_USERS_EDITROLEDIALOG_ACTIONS_CANCEL')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isSaving}
            className="w-full sm:w-auto bg-amber-500 text-white hover:bg-amber-600"
          >
            {isSaving && <Loader2 className="size-4 animate-spin mr-2" />}
            {t('ADMIN_USERS_EDITROLEDIALOG_ACTIONS_CONFIRM')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
